import React, {
  ReactElement,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDataLoaderNew } from 'data-loader';
import {
  useParams,
  useBeforeUnload,
  useBlocker,
  Location,
} from 'react-router-dom';
import {
  DetailFieldConflict,
  PLAN_TASKS_UPDATED,
  ProjectPermission,
  ProjectPlan,
  TASK_UPDATED,
  useProjectPlanService,
} from 'nekst-api';
import useFeatureToggleService from 'feature-toggle';
import ProjectAuthorizationScope from '../../view/ProjectAuthorizationScope';
import ProjectContext, { ProjectBreadcrumb, ProjectReactContext } from '../../view/ProjectContext';
import MainBlock from '../../../shared/web/layout/MainBlock';
import { Breadcrumb } from '../../../shared/web/layout/Breadcrumbs';
import { TextField } from 'ui-builder';

import styles from '../LaunchProjectWrapper.module.scss';

export interface AdditionalWorkflowLaunchWrapperProps {
  children: ReactElement | ReactElement[];
}

interface ConflictsContextValue {
  conflicts: DetailFieldConflict[];
  hasConflicts: boolean;
  isLoading: boolean;
}

const ConflictsReactContext = createContext<ConflictsContextValue>({
  conflicts: [],
  hasConflicts: false,
  isLoading: true,
});

export function useConflictsContext() {
  return useContext(ConflictsReactContext);
}

function AdditionalWorkflowBreadcrumbs() {
  return (
    <ProjectBreadcrumb>
      <Breadcrumb text="Add Workflow" />
    </ProjectBreadcrumb>
  );
}

function AdditionalWorkflowPlanName(props: { plan?: ProjectPlan }) {
  if (props.plan?._sourcePlans?.length) {
    const names = props.plan._sourcePlans.map((sp) => sp.name).join(', ');
    return (
      <span className={styles.planName}>
        <b>Workflow:</b>
        &nbsp;
        {names}
      </span>
    );
  } else {
    return null;
  }
}

function AdditionalWorkflowFormPrompt(props: { basePath: string }) {
  useBeforeUnload(
    useCallback(
      (e: BeforeUnloadEvent) => {
        e.preventDefault();
        e.returnValue = '';
      },
      [],
    ),
    { capture: true },
  );

  const blocker = useBlocker(
    useCallback(
      ({ nextLocation }: { currentLocation: Location; nextLocation: Location }) => {
        if (
          !nextLocation.pathname.startsWith(props.basePath)
          && !nextLocation.search.includes('?afterLaunch=1')
        ) {
          // eslint-disable-next-line no-alert
          return !window.confirm(
            'By exiting before completing the steps to Launch new tasks, no new tasks will be applied to the transaction.',
          );
        } else {
          return false;
        }
      },
      [],
    ),
  );

  const prevState = useRef(blocker.state);

  useEffect(() => {
    if (blocker.state === 'blocked') {
      blocker.reset();
    }
    prevState.current = blocker.state;
  }, [blocker]);

  return null;
}

function ConflictsProvider(props: {
  projectId: number;
  planId: number;
  children: ReactElement | ReactElement[];
}) {
  const projectPlanService = useProjectPlanService();
  const featureToggleService = useFeatureToggleService();
  const isEnabled = featureToggleService.isApplyAdditionalWorkflowEnabled();

  const { data: conflicts, loading: isLoading } = useDataLoaderNew<DetailFieldConflict[]>(
    async () => {
      if (isEnabled) {
        return projectPlanService.getConflicts(props.projectId, props.planId);
      } else {
        return [];
      }
    },
    {
      dependencies: [props.projectId, props.planId],
      events: [TASK_UPDATED, PLAN_TASKS_UPDATED],
    },
  );

  const value = useMemo(() => ({
    conflicts: conflicts || [],
    hasConflicts: (conflicts || []).length > 0,
    isLoading,
  }), [JSON.stringify(conflicts), isLoading]);

  return (
    <ConflictsReactContext.Provider value={value}>
      {props.children}
    </ConflictsReactContext.Provider>
  );
}

function AdditionalWorkflowPlanProvider(props: {
  planId: number;
  projectId: number;
  children: ReactElement | ReactElement[];
}) {
  const outerContext = useContext(ProjectReactContext);
  const projectPlanService = useProjectPlanService();

  const [plan, setPlan] = useState<ProjectPlan>();

  const loadPlan = async (forced?: boolean) => {
    if (forced || !plan) {
      const plans = await projectPlanService.getProjectPlans(props.projectId);
      const found = plans.find((p) => p.id === props.planId);
      if (found) {
        setPlan(found);
      }
    }
  };

  useEffect(() => {
    loadPlan();
  }, [props.planId]);

  const excludeGroupFromProjectPlan = (groupId: number) => {
    if (plan) {
      setPlan({
        ...plan,
        excludedGroupsIds: [...plan.excludedGroupsIds, groupId],
      });
    }
  };

  const includeGroupToProjectPlan = (groupId: number) => {
    if (plan) {
      setPlan({
        ...plan,
        excludedGroupsIds: plan.excludedGroupsIds.filter((item) => item !== groupId),
      });
    }
  };

  const setExcludedGroups = (groupsIds: number[]) => {
    if (plan) {
      setPlan({
        ...plan,
        excludedGroupsIds: groupsIds,
      });
    }
  };

  const contextValue = useMemo(
    () => ({
      ...outerContext,
      plan,
      loadPlan,
      isPlanLoaded: () => !!plan,
      excludeGroupFromProjectPlan,
      includeGroupToProjectPlan,
      setExcludedGroups,
    }),
    [outerContext.assignments, plan],
  );

  return (
    <ProjectReactContext.Provider value={contextValue}>
      <MainBlock
        title={<TextField source="name" />}
        titleLine2={<AdditionalWorkflowPlanName plan={plan} />}
      >
        <ConflictsProvider projectId={props.projectId} planId={props.planId}>
          {props.children}
        </ConflictsProvider>
      </MainBlock>
    </ProjectReactContext.Provider>
  );
}

export default function AdditionalWorkflowLaunchWrapper(props: AdditionalWorkflowLaunchWrapperProps) {
  const params = useParams();

  const projectId = parseInt(params.id!, 10);
  const planId = parseInt(params.planId!, 10);

  const basePath = `/transactions/${projectId}/plans/${planId}/launch`;

  return (
    <ProjectAuthorizationScope
      id={projectId}
      permissionToCheck={ProjectPermission.UPDATE}
    >
      <ProjectContext projectId={projectId}>
        <AdditionalWorkflowBreadcrumbs />
        <AdditionalWorkflowPlanProvider planId={planId} projectId={projectId}>
          {props.children}
        </AdditionalWorkflowPlanProvider>
        <AdditionalWorkflowFormPrompt basePath={basePath} />
      </ProjectContext>
    </ProjectAuthorizationScope>
  );
}
